/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import parse from 'html-react-parser';
import isEqual from 'lodash.isequal';
import isString from 'lodash.isstring';
import pickBy from 'lodash.pickby';
import {
  AccountHashMapKeyType,
  AccountHashMapType
} from '@ion/core/src/entities/Accounts';
import {GenericObject} from '@ion/core/src/types/entities';
import {createRegExp, splitBySpecialCharacters} from '@ion/core/src/utils/StringUtil';
import {defineBreadcrumbs} from '../breadcrumbs/BreadcrumbUtils';
import {resolveFilter} from '../filters/FilterUtils';

export interface BuildSearchResultBreadcrumbType {
  searchedData: AccountHashMapType[];
  classes: {
    [key: string]: string;
  };
  accountData: AccountHashMapType;
  userAccount: string;
}

export interface GroupedFiltersType {
  [key: number]: {
    crumb: JSX.Element[];
    crumbRaw: {
      [key: string]: string;
    }[];
    items: AccountHashMapType[];
  }
}

/**
 * @handleAccountSearchChange
 *
 * function that searches through passed accounts to match the passed
 * search value and calls the passed setSearchedFilterData function with the
 * results in object properties, including breadcrumbs if its a group filter
 *
 * ```typescript
 * import {handleAccountSearchChange} from '@ion/dls/src/components/search';
 *
 *   handleAccountSearchChange({
 *     searchValue : 'abc',
 *     isGroupsFilter : true,
 *     accounts,
 *     setSearchedGroupData,
 *     classes,
 *     userAccount,
 *     filterData,
 *     setSearchedFilterData : (val) => void
 *   })
 *
 *   // setSearchedFilterData({
 *
 *      })
 * ```
 */
export function handleAccountSearchChange({
  searchValue,
  isGroupsFilter,
  accounts,
  setSearchedGroupData,
  classes,
  userAccount,
  filterData,
  setSearchedFilterData
}) {
  if (searchValue) {
    if (isGroupsFilter) {
      const searchedData = Object.entries(accounts)?.map((
        // @ts-ignore
        [key, {crumb, crumbRaw, ...dataItem}]
      ) => ({
        ...dataItem, id : parseInt(key)
      })).filter(value => {
        return value?.title.toLowerCase().includes(searchValue?.toLowerCase()) || value?.id === Number(searchValue);
      });

      if (searchedData?.length) {
        setSearchedGroupData(
          buildSearchResultBreadcrumb({
            searchedData,
            classes,
            accountData : accounts,
            userAccount
          })
        );
      } else {
        setSearchedGroupData({});
      }
    } else {
      const searchedData = filterData.filter(dataItem => {
        const {label} = resolveFilter(dataItem);
        return label.toLowerCase().includes(searchValue?.toLowerCase());
      });
      setSearchedFilterData(searchedData);
    }
  } else {
    if (isGroupsFilter) {
      setSearchedGroupData(accounts);
    } else {
      setSearchedFilterData(filterData);
    }
  }
}

/**
 * @buildSearchResultBreadcrumb
 *
 * Will recursively crawl the list of searchedData and add crumb data
 * to each one. The list can then be further filtered based on existing search data.
 *
 * ```typescript
 * import {buildSearchResultBreadcrumb} from '@ion/dls/src/components/search';
 *
 *   buildSearchResultBreadcrumb({
 *     searchValue : 'abc',
 *     isGroupsFilter : true,
 *     accounts,
 *     setSearchedGroupData,
 *     classes,
 *     userAccount,
 *     filterData,
 *     setSearchedFilterData : (val) => void
 *   })
 *
 *   // [
 *        {
 *          crumb    : item.crumb,
 *          crumbRaw : {label: 'CrumbOne', onClick: () => void},
 *          items    : [item1, item2]
 *        },
 *        {
 *          crumb    : item.crumb,
 *          crumbRaw : {label: 'CrumbA', onClick: () => void},
 *          items    : [itemA, itemB]
 *        }
 *     ]
 * ```
 */
export const buildSearchResultBreadcrumb = ({
  searchedData,
  classes,
  accountData,
  userAccount
}: BuildSearchResultBreadcrumbType) => {
  const accountDataCopy = {};
  searchedData.forEach(item => {
    const crumbs: {label: string}[] = [];
    const {id: itemId} = item;
    function addCrumbs(account) {
      const parentId = account.parentId;
      if (parentId) {
        crumbs.unshift({label: accountData[parentId].title});
        return addCrumbs(accountData[parentId]);
      }
      userAccount && crumbs.unshift({label: userAccount});
    };
    addCrumbs(item);
    const displayCrumbs = defineBreadcrumbs({crumbs, classes});
    accountDataCopy[itemId] = item;
    accountDataCopy[itemId].crumb = displayCrumbs;
    accountDataCopy[itemId].crumbRaw = crumbs;
  });
  const groupedFilters = groupSearchFilters(accountDataCopy);
  return groupedFilters;
};

/**
 * @buildSearchResultBreadcrumb
 *
 * takes searched data and builds a list of objects with components to be rendered,
 * raw crumb data, and renderable crumb components
 *
 * ```typescript
 * import {handleAccountSearchChange} from '@ion/dls/src/components/search';
 *
 *   handleAccountSearchChange({
 *     searchValue : 'abc',
 *     isGroupsFilter : true,
 *     accounts,
 *     setSearchedGroupData,
 *     classes,
 *     userAccount,
 *     filterData,
 *     setSearchedFilterData : (val) => void
 *   })
 *
 *   // [
 *        {
 *          crumb    : item.crumb,
 *          crumbRaw : {label: 'CrumbOne', onClick: () => void},
 *          items    : [item1, item2]
 *        },
 *        {
 *          crumb    : item.crumb,
 *          crumbRaw : {label: 'CrumbA', onClick: () => void},
 *          items    : [itemA, itemB]
 *        }
 *     ]
 * ```
 */
export function groupSearchFilters(searchedData: AccountHashMapKeyType) {
  const groupedFilters: GroupedFiltersType = {};
  Object.values(searchedData).forEach(item => {
    if (Object.keys(groupedFilters).length) {
      let matchedKey;
      const match = Object.entries(groupedFilters).find(([key, group]) => {
        if (isEqual(group.crumbRaw, item.crumbRaw)) {
          matchedKey = key;
          return true;
        };
        return false;
      });
      if (match) {
        groupedFilters[matchedKey].items.push(item);
      } else {
        groupedFilters[Object.keys(groupedFilters).length + 1] = {
          crumb    : item.crumb,
          crumbRaw : item.crumbRaw,
          items    : [item]
        };
      }
    } else {
      groupedFilters[Object.keys(groupedFilters).length + 1] = {
        crumb    : item.crumb,
        crumbRaw : item.crumbRaw,
        items    : [item]
      };
    }
  });
  return groupedFilters;
}

/**
 * The filterItems utility handles to filter items
 *
 * ```typescript
 *
 * import {filterItems} from '@ion/dls/src/util';
 *
 * data.filterOn = filterItems(filterOn, search);
 *
 * ```
 */
export const filterItems = (filterOn: any, string: any) => {
  return {...filterOn, ...pickBy(string, isString)};
};

/**
 * The renderName utility just returns the html parsed search result of the search display name based on
 * the best match search term and the associated status of the search results.
 *
 * ```typescript
 *
 *  import {renderName} from '@ion/dls/src/util';
 *
 *  // default use case, where the match search term highlighted by bold
 *  <div>{renderName(assetName, 'asset')}</div>
 *
 *  // 2nd use case, where the match search term highlighted by bold and light blue color
 *  <div>{renderName(assetName, 'asset', true)}</div>
 *
 * ```
 *
 * @param name         The display name of the the search result
 * @param search       The search term to be used in the search
 * @param isAssociated A boolean value indicating whether the search is for an associated asset or not.
 * @returns HTML parsed search result
 */
export const renderName = (name: string, search: string, isAssociated = false) => {
  // private function to bold the text...
  const boldText = (text: string, isAssociated = false) =>
    `<span id=${isAssociated ? 'associate' : ''}match>${text}</span>`
  ;
  const regex = createRegExp(search, 'ig');
  const actual = name.match(regex)?.[0];
  // if the search term is as a part of the search result
  if (actual) {
    return parse(name.replace(regex, boldText(actual, isAssociated)));
  }
  // if the search term is not as a part of the search result, then
  // we just try to see what are the possible matches that are available
  // in the search result
  let resolved = [name];
  splitBySpecialCharacters(search, true).forEach((match: string) => {
    const regex = createRegExp(match, 'ig');
    const actual = name.match(regex)?.[0];
    const temp = [];
    resolved.forEach((res: string | GenericObject) => {
      // resolved array may have html parsed element and string, we need to
      // parse only the text not html elements...
      if (isString(res)) {
        const parsed = (parse(res.replace(regex, boldText(actual, isAssociated))) as any);
        // parsed may be a string or array...
        if (Array.isArray(parsed)) {
          temp.push(...parsed);
        } else {
          temp.push(parsed);
        }
      } else {
        // if the res is not a string, then we can just push to temp array...
        temp.push(res);
      }
    });
    // for every iteration of search term assign temp aray to resolved...
    resolved = temp;
  });
  // at last returning the resolved which is combination of html
  // elements(bold string - matched with search term) and normal text
  return resolved;
};
